import { useMemo } from 'react';
import { Config, Params, OperatingScenario } from '../types/config';
import { calculateYearlyCostsPerKwh, calculateNPV, calculateBreakeven, calculateIRR } from '../utils/financialCalculations';
import { generateDailyPowerFlow, calculateEnergyMix, calculateBatteryLifespan } from '../utils/energyCalculations';
import { calculateCarbonEmissions, calculateEnvironmentalEquivalents } from '../utils/carbonCalculations';

/**
 * Custom hook for performing all financial, energy, and environmental calculations
 * 
 * This hook serves as a central calculation engine for the entire application, consolidating
 * all computational logic in one place and memoizing results for performance optimization.
 * It takes the current application state as input and produces all derived values needed
 * for dashboard visualization and analysis.
 * 
 * Key features:
 * - Centralizes all calculations to avoid redundant computations
 * - Uses memoization to prevent recalculation unless inputs change
 * - Creates formatted data structures ready for visualization components
 * - Separates calculation logic from UI components
 * 
 * @param config - System configuration containing current and expanded scenarios
 * @param params - Financial and technical parameters
 * @param scenario - Current operating scenario ('current' or 'expanded')
 * @returns Object containing all calculation results ready for use in components
 */
export const useCalculations = (
  config: Config,
  params: Params,
  scenario: OperatingScenario
) => {
  /**
   * Annual and cumulative costs for all energy options
   * 
   * Calculates yearly costs per kWh, annual costs, and cumulative costs
   * for grid-only, grid-tied solar, solar+battery, and generator options.
   */
  const costs = useMemo(() => 
    calculateYearlyCostsPerKwh(config, params, scenario), 
    [config, params, scenario]
  );
  
  /**
   * Net Present Value results for both operating scenarios
   * 
   * Computes NPV of costs, savings comparisons, LCOE values, and terminal values
   * for both the current and expanded operating scenarios.
   */
  const npvResults = useMemo(() => ({
    current: calculateNPV(config, params, 'current'),
    expanded: calculateNPV(config, params, 'expanded')
  }), [config, params]);
  
  /**
   * Breakeven (payback) period calculations
   * 
   * Determines when the investment in solar+battery will break even compared to
   * grid-only or grid-tied solar options.
   */
  const breakeven = useMemo(() => 
    calculateBreakeven(costs, config, params, scenario), 
    [costs, config, params, scenario]
  );
  
  /**
   * Internal Rate of Return calculations
   * 
   * Calculates IRR for the solar+battery investment compared to grid-only option.
   */
  const irr = useMemo(() => 
    calculateIRR(config, params, scenario),
    [config, params, scenario]
  );
  
  /**
   * Battery lifespan in years
   * 
   * Converts battery cycle life to calendar years based on daily cycling.
   */
  const batteryLifespanYears = useMemo(() => 
    calculateBatteryLifespan(params.batteryCycles),
    [params.batteryCycles]
  );
  
  /**
   * Hourly power flow data for energy flow visualization
   * 
   * Creates an array of hourly data points showing the contribution of different
   * energy sources (solar, battery, grid) throughout a typical day.
   */
  const dailyPowerFlow = useMemo(() => 
    generateDailyPowerFlow(config, scenario),
    [config, scenario]
  );
  
  /**
   * Energy mix data for pie chart visualization
   * 
   * Calculates the proportion of energy from different sources (direct solar,
   * battery, grid) for the pie chart in the Energy Flow tab.
   */
  const energyMix = useMemo(() => 
    calculateEnergyMix(config, scenario),
    [config, scenario]
  );
  
  /**
   * Carbon emissions and offset costs
   * 
   * Calculates emissions, offset costs, and savings for all energy options,
   * providing both annual and 25-year lifetime values.
   */
  const carbonEmissions = useMemo(() => 
    calculateCarbonEmissions(config, params, scenario),
    [config, params, scenario]
  );
  
  /**
   * Environmental equivalents of carbon savings
   * 
   * Translates carbon savings into relatable environmental equivalents
   * (e.g., cars off the road, trees planted) for the Environmental Impact tab.
   */
  const environmentalEquivalents = useMemo(() => 
    calculateEnvironmentalEquivalents(carbonEmissions.batteryVsGridSavings25Year),
    [carbonEmissions.batteryVsGridSavings25Year]
  );
  
  /**
   * Combined chart data for LCOE comparison visualization
   * 
   * Formats cost data into a structure optimal for the line chart comparing
   * levelized cost of energy (LCOE) over time for all energy options.
   */
  const combinedChartData = useMemo(() => 
    costs.gridOnly.map(item => {
      const gridTiedItem = costs.gridTied.find(i => i.year === item.year);
      const batteryItem = costs.battery.find(i => i.year === item.year);
      const generatorItem = costs.generator.find(i => i.year === item.year);
      
      return {
        year: item.year,
        gridOnly: item.value,
        gridTied: gridTiedItem ? gridTiedItem.value : 0,
        battery: batteryItem ? batteryItem.value : 0,
        generator: generatorItem ? generatorItem.value : 0
      };
    }),
    [costs]
  );
  
  /**
   * Cumulative cost data for financial comparison visualization
   * 
   * Formats cumulative cost data into millions of AED for the line chart comparing
   * total accumulated costs over time for all energy options.
   */
  const cumulativeCostData = useMemo(() => 
    costs.gridOnly.map(item => {
      const gridTiedItem = costs.gridTied.find(i => i.year === item.year);
      const batteryItem = costs.battery.find(i => i.year === item.year);
      const generatorItem = costs.generator.find(i => i.year === item.year);
      
      return {
        year: item.year,
        gridOnly: item.cumulativeCost / 1000000,  // Convert to millions
        gridTied: gridTiedItem ? gridTiedItem.cumulativeCost / 1000000 : 0,
        battery: batteryItem ? batteryItem.cumulativeCost / 1000000 : 0,
        generator: generatorItem ? generatorItem.cumulativeCost / 1000000 : 0
      };
    }),
    [costs]
  );
  
  /**
   * Lifecycle costs table data
   * 
   * Creates a table of LCOE values at key milestone years (1, 5, 10, 15, 20, 25)
   * for all energy options, used in the Cost Comparison tab.
   */
  const lifecycleCostsTable = useMemo(() => {
    const years = [1, 5, 10, 15, 20, 25];
    
    return years.map(year => {
      const yearData = {
        gridOnly: costs.gridOnly.find(i => i.year === year),
        gridTied: costs.gridTied.find(i => i.year === year),
        battery: costs.battery.find(i => i.year === year),
        generator: costs.generator.find(i => i.year === year),
      };
      
      return {
        year,
        gridOnly: yearData.gridOnly ? yearData.gridOnly.value : 0,
        gridTied: yearData.gridTied ? yearData.gridTied.value : 0,
        battery: yearData.battery ? yearData.battery.value : 0,
        generator: yearData.generator ? yearData.generator.value : 0
      };
    });
  }, [costs]);
  
  // Return a comprehensive object containing all calculation results
  return {
    costs,
    npvResults,
    breakeven,
    irr,
    batteryLifespanYears,
    dailyPowerFlow,
    energyMix,
    carbonEmissions,
    environmentalEquivalents,
    combinedChartData,
    cumulativeCostData,
    lifecycleCostsTable
  };
};